// TODO(BNCH-5877) Remove once we convert TableEditable to Typescript
/* eslint-disable @typescript-eslint/no-explicit-any */
// TODO: This file was automatically migrated to TypeScript by `dev typescript migrate`.
//  The eslint disable comment above should be removed and the resulting errors fixed.
import {noop} from '@benchling/js-utils';
import classNames from 'classnames';
import React from 'react';
import isEqual from 'react-fast-compare';

import {ContextMenuOption} from 'components/ContextMenu';
import {
  EMPTY_SEARCH_RESULT_PROPS,
  forwardSearchResultProps,
  MediocreSearchResultProps,
} from 'notebook/mediocre/search/SearchResultProps';
import {SELECTORS_LINK, UNSUPPORTED_OPERATION} from 'notebook/simple-tables/SimpleSpreadsheetEvalMixin';
import withSimpleSpreadsheetEvaluation from 'notebook/simple-tables/withSimpleSpreadsheetEvaluation';
import TableEditable from 'notebook/tables/core-ui/TableEditable';
import {EvaluatedCell, OutputTableMap} from 'notebook/tables/excess/Evaluator';
import {ReorderOptions} from 'notebook/tables/excess/ops/TableOps';
import {SimpleTableOpProvider} from 'notebook/tables/spreadsheets/TableOpContext';
import withSpreadsheetEvaluation, {
  ComponentProps,
  EnhancedProps as SpreadsheetEvaluationEnhancedProps,
} from 'notebook/tables/spreadsheets/withSpreadsheetEvaluation';
import * as TableConstants from 'notebook/tables/TableConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import memoizeLastCall from 'util/memoizeLastCall';

/**
 * These are the properties that we expect to get mixed into our SimpleTableEditable so other components
 * don't need to implement these.
 */
export interface EnhancedProps extends Pick<
  SpreadsheetEvaluationEnhancedProps,
  | 'cellColors'
  | 'computedTables'
  | 'editingTable'
  | 'tableNamespace'
  | 'tryInsert'
  | 'canInsert'
  | 'setMultiEditingForTable'
> {}

/**
 * These are the props that we need callers of SimpleTableEditable to provide.
 *
 * TODO(BNCH-5877): this should extend TableEditableProps as they provide similar info
 */
export interface SimpleTableEditableProps extends MediocreSearchResultProps {
  additionalFunctions?: Array<TableConstants.TableFunction>;
  additionalSettings?: Array<TableConstants.TableSetting>;
  allowReorderDimensions?: TableConstants.TableReorderLocks;
  autoCollapseLimit?: number;
  /** Allows adding columns automatically by editing the "ghost cells" to the right of the existing cells */
  canAddColumnsFreeform?: boolean;
  canAddRemoveColumns: boolean;
  enableColumnResizing?: boolean;
  cid: string;
  dataTestAttrs?: DataTestAttrs;
  // If not specified, table deletion is disabled.
  deleteHandler?: () => void;
  // Whether or not to disable the maximize table capability. Defaults to true.
  disableMaximizedTables?: boolean;
  editWhileTableLockedWarning?: string;
  getEditWhileCellLockedWarning?: () => string;
  allowMergingCells?: boolean;
  allowSingleInputs?: boolean;
  enableFillDown?: boolean;
  enableFormulas: boolean;
  // should be required, but allows null
  fileId: string | null;
  forceCollapsed?: boolean;
  /**
   * Manually specify the row count to be displayed on the collapsed table header. If not specified
   * the row count will be determined from the table length.
   */
  forceRowCount?: number;
  hideRowCount?: boolean;

  // Function to get additional context menu options for the row axis
  getExtraRowAxisContextMenuOptions?: (
    selectionRange: TableConstants.SelectionRange | null
  ) => Array<ContextMenuOption> | null;

  // Function that given the selection range, returns additional menu items to display in the context menu.
  // '-' represents a separator.
  getExtraContextMenuOptions?: (
    selectionRange: TableConstants.SelectionRange | null,
    updateColumnConstraint?: (index: number, columnConstraint: TableConstants.ColumnConstraint) => void
  ) => Array<ContextMenuOption> | null;

  hasPlaceholderRows?: boolean;

  // items to render in the title bar when viewer == false
  headerControls?: TableConstants.TableHeaderControls;
  leftHeaderControls?: TableConstants.TableHeaderControls;

  hideColumnHeaderIcons?: boolean;
  hideFormulaBox?: boolean;
  hideFooter?: boolean;
  icon?: any;
  indentation: number;
  index: any;
  insertableColumnInfos?: Array<TableConstants.InsertableColumnInfo>;
  insertItemHandler: any;
  insertRowsAbove?: (index: number, start: number, end: number) => void;
  insertRowsBelow?: (index: number, start: number, end: number) => void;
  reorderRowsOrColumns?: (index: number, options: ReorderOptions) => void;
  isCollapsible: boolean;
  isCompactBottomSpacing: boolean;
  isLeftAlignColumnHeader?: boolean;
  isLeftAlignRowHeader?: boolean;
  isMaximized?: boolean;
  isSplitScreen?: boolean;
  isFocused?: boolean;
  isParentLockedWithEditWarn?: () => boolean;
  isProtected?: boolean;
  /**
   * Returns whether or not a table name be used by this table.
   * While SimpleTableEditable is currently isolated, we are moving towards many usages of it, namely
   * DataTableEditable and DataTableConfigEditable, to have formula, so their names cannot conflict
   * with any other table in the note.
   */
  isTableNameUsed: (tableName: string) => boolean;
  isTableHeaderFaded?: boolean;
  maximumHeight?: TableConstants.MaximumTableHeightType;

  // true if the user cannot edit, whether or not they are currently in an editing context
  // (e.g., the user does not have permission)
  locked: boolean;

  mouseDownHandler: any;
  mouseOverHandler: any;
  moveHandler: any;
  /**
   * If supplied, this function will be called before columns are deleted. Call the doDelete parameter
   * to actually delete the columns. This can be used to, for example, show a confirmation dialog before
   * deletion.
   */
  onDeleteColumns?: (
    index: number,
    start: number,
    end: number,
    doDelete: (index: number, start: number, end: number) => void
  ) => void;
  /**
   * If supplied, this function will be called before contiguous rows are deleted. Call the doDelete
   * parameter to actually delete the rows. This can be used to, for example, show a confirmation
   * dialog before deletion.
   *
   * Note: this callback is bypassed for row deletions via deleteTableRowsNonContiguous.
   */
  onDeleteRows?: (
    index: number,
    start: number,
    end: number,
    doDelete: (index: number, start: number, end: number) => void
  ) => void;
  onSimpleTableEditableMount?: (comp: any) => void;
  onSimpleTableEditableUnmount?: () => void;
  onComputedTableChanged?: (computedTable: Array<Array<EvaluatedCell>>) => void;
  preserveMergedCellsInPaste?: boolean;
  requestSelection: any;
  restoreState: any;
  selectTable?: any;
  setClipboard: any;
  setSelectionAbove: any;
  setSelectionBelow: any;
  showExcessLabels: boolean;
  setMaximizedTable?: (focusedTableProvenanceUuid: string | null) => void;
  setSplitScreenTable?: (focusedTableProvenanceUuid: string | null) => void;
  suppressTitleBar?: boolean;
  storeState: any;
  tabHandler: any;
  table: TableConstants.Table;
  tableStatus?: TableConstants.TableIndicatorStatus | null;
  tableLabel?: string;
  updateHandler: (index: number, {table, selection}: {table: TableConstants.Table; selection: any}) => void;

  // true if the user cannot edit because they are not currently in an editing context, e.g. in a print view
  // (locked should also be true)
  viewer?: boolean;
  canRename?: boolean;

  getDisallowedColumnNames?: () => Array<string>;

  errorRows?: Array<boolean>;
  warningRows?: Array<boolean>;
}

export class SimpleTableEditable extends React.PureComponent<SimpleTableEditableProps & EnhancedProps> {
  constructor(props: SimpleTableEditableProps & EnhancedProps) {
    super(props);
  }

  computedTable = memoizeLastCall(
    (computedTables: OutputTableMap, table: TableConstants.Table): Array<Array<EvaluatedCell>> =>
      computedTables && computedTables[table.name] ? computedTables[table.name] : [[]]
  );

  getComputedTable = (): Array<Array<EvaluatedCell>> =>
    this.computedTable(this.props.computedTables, this.getTable(this.props));

  static selectorsLink = SELECTORS_LINK;

  // Boilerplate props that can be passed into a SimpleTableEditable when the table is not embedded in a
  // larger editing context, e.g. a modal instead of an entry.
  static standaloneProps = {
    fileId: null,
    index: 0,
    indentation: 0,
    insertItemHandler: UNSUPPORTED_OPERATION,
    mouseDownHandler: null,
    mouseOverHandler: null,
    moveHandler: null,
    requestSelection: null,
    restoreState: UNSUPPORTED_OPERATION,
    setClipboard: noop,
    setSelectionAbove: noop,
    setSelectionBelow: noop,
    storeState: UNSUPPORTED_OPERATION,
    tabHandler: UNSUPPORTED_OPERATION,
    updateHandler: UNSUPPORTED_OPERATION,
    showExcessLabels: false,
    ...EMPTY_SEARCH_RESULT_PROPS,
  };

  static defaultProps = {
    canAddColumnsFreeform: false,
    canAddRemoveColumns: true,
    isTableNameUsed: (name: string): false => false,
    isTableHeaderFaded: false,
    enableFormulas: false,
    isCollapsible: false,
    isCompactBottomSpacing: false,
  };

  override componentDidMount(): void {
    if (this.props.onSimpleTableEditableMount) {
      this.props.onSimpleTableEditableMount(this);
    }
    if (this.props.onComputedTableChanged) {
      this.props.onComputedTableChanged(
        this.computedTable(this.props.computedTables, this.getTable(this.props))
      );
    }
  }

  override componentDidUpdate(prevProps: SimpleTableEditableProps & EnhancedProps): void {
    const computedTables = this.props.computedTables;
    if (this.props.onComputedTableChanged && !isEqual(computedTables, prevProps.computedTables)) {
      this.props.onComputedTableChanged(this.computedTable(computedTables, this.getTable(this.props)));
    }
  }

  override componentWillUnmount(): void {
    if (this.props.onSimpleTableEditableUnmount) {
      this.props.onSimpleTableEditableUnmount();
    }
  }

  /**
   * Accessed externally via a ref.
   */
  getTable = (props: SimpleTableEditableProps & EnhancedProps): TableConstants.Table => props.table;

  getTableCid = (): string => this.props.cid;

  /* Required by SpreadsheetEvalMixin */
  shouldDisableFormulas(): boolean {
    return !this.props.enableFormulas;
  }

  handleTableUpdate(
    index: number,
    {
      table,
      selection,
    }: {table: TableConstants.Table; selection: {cid: string; start: number; end: number} | null}
  ): void {
    this.props.updateHandler(index, {table, selection});
  }

  _isMultiEditing = (): boolean => this.props.editingTable === this.getTable(this.props).name;

  getComputedTables = (): OutputTableMap => this.props.computedTables;

  getTableWithProps = (): TableConstants.Table => this.getTable(this.props);

  override render(): JSX.Element {
    return (
      <div
        className={classNames({
          'tableEditable-maximizedWrapper':
            // only set the maximizedWrapper if the table is not focused.  isFocused is used to
            // display the table in a collapsed highlighted state to show which table is currently open in a
            // Table View.
            (this.props.isMaximized || this.props.isSplitScreen) && !this.props.isFocused,
        })}
        {...getTestProps(this.props.dataTestAttrs)}
      >
        <SimpleTableOpProvider
          cid={this.props.cid}
          getTable={this.getTableWithProps}
          getComputedTables={this.getComputedTables}
          updateHandler={this.props.updateHandler}
          onDeleteColumns={this.props.onDeleteColumns}
          onDeleteRows={this.props.onDeleteRows}
          onReorderRowsOrColumns={this.props.reorderRowsOrColumns}
          onInsertRowsBelow={this.props.insertRowsBelow}
          onInsertRowsAbove={this.props.insertRowsAbove}
          allowMergingCells={this.props.allowMergingCells}
          allowSingleInputs={this.props.allowSingleInputs}
        >
          <TableEditable
            errorRows={this.props.errorRows}
            warningRows={this.props.warningRows}
            autoCollapseLimit={this.props.autoCollapseLimit}
            additionalSettings={this.props.additionalSettings}
            allowReorderDimensions={this.props.allowReorderDimensions}
            canAddColumnsFreeform={this.props.canAddColumnsFreeform}
            canAddRemoveColumns={this.props.canAddRemoveColumns}
            enableColumnResizing={this.props.enableColumnResizing}
            canRename={this.props.canRename ?? true}
            headerControls={this.props.headerControls}
            leftHeaderControls={this.props.leftHeaderControls}
            locked={this.props.locked}
            tableStatus={this.props.tableStatus}
            fileId={this.props.fileId ?? ''}
            cid={this.props.cid}
            table={this.props.table}
            computedTable={this.computedTable(this.props.computedTables, this.getTable(this.props))}
            cellColors={this.props.cellColors[this.props.table.name]}
            additionalFunctions={this.props.additionalFunctions}
            showExcessLabels={this.props.showExcessLabels}
            showFormulaBox={!this.props.hideFormulaBox}
            hideFooter={this.props.hideFooter}
            hideColumnHeaderIcons={this.props.hideColumnHeaderIcons}
            index={this.props.index}
            indentation={this.props.indentation}
            disableMergingCells={!this.props.allowMergingCells}
            disableFormulas={this.shouldDisableFormulas()}
            enableFillDown={this.props.enableFillDown}
            hasPlaceholderRows={this.props.hasPlaceholderRows}
            isParentLockedWithEditWarn={this.props.isParentLockedWithEditWarn}
            isProtected={this.props.isProtected}
            editWhileTableLockedWarning={this.props.editWhileTableLockedWarning}
            getEditWhileCellLockedWarning={this.props.getEditWhileCellLockedWarning}
            getExtraRowAxisContextMenuOptions={this.props.getExtraRowAxisContextMenuOptions}
            tryInsert={this.props.tryInsert}
            canInsert={this.props.canInsert}
            setMultiEditingForTable={this.props.setMultiEditingForTable}
            openManageSelectorsModal={noop}
            disableLegacyHeader={true}
            disableMaximizedTables={this.props.disableMaximizedTables ?? true}
            setMaximizedTable={this.props.setMaximizedTable ?? noop}
            setSplitScreenTable={this.props.setSplitScreenTable ?? noop}
            isMaximized={this.props.isMaximized ?? false}
            isSplitScreen={this.props.isSplitScreen ?? false}
            isFocused={this.props.isFocused ?? false}
            insertableColumnInfos={this.props.insertableColumnInfos}
            isTableNameUsed={this.props.isTableNameUsed}
            isTableHeaderFaded={this.props.isTableHeaderFaded}
            tableNamespace={this.props.tableNamespace}
            viewer={this.props.viewer}
            isCollapsible={this.props.isCollapsible}
            isCompactBottomSpacing={this.props.isCompactBottomSpacing}
            forceCollapsed={this.props.forceCollapsed}
            forceRowCount={this.props.forceRowCount}
            hideRowCount={this.props.hideRowCount}
            deleteHandler={this.props.deleteHandler}
            insertItemHandler={this.props.insertItemHandler}
            mouseDownHandler={this.props.mouseDownHandler}
            mouseOverHandler={this.props.mouseOverHandler}
            moveHandler={this.props.moveHandler}
            requestSelection={this.props.requestSelection}
            restoreState={this.props.restoreState}
            selectTable={this.props.selectTable}
            setClipboard={this.props.setClipboard}
            setSelectionAbove={this.props.setSelectionAbove}
            setSelectionBelow={this.props.setSelectionBelow}
            storeState={this.props.storeState}
            tableLabel={this.props.tableLabel}
            tabHandler={this.props.tabHandler}
            updateHandler={this.props.updateHandler}
            isLeftAlignColumnHeader={this.props.isLeftAlignColumnHeader}
            isLeftAlignRowHeader={this.props.isLeftAlignRowHeader}
            getExtraContextMenuOptions={this.props.getExtraContextMenuOptions}
            getDisallowedColumnNames={this.props.getDisallowedColumnNames}
            icon={this.props.icon}
            selectorsLink={SELECTORS_LINK}
            selectorsEnabled={false}
            multiEditing={this._isMultiEditing()}
            suppressTitleBar={this.props.suppressTitleBar}
            preserveMergedCellsInPaste={this.props.preserveMergedCellsInPaste}
            maximumHeight={this.props.maximumHeight}
            {...forwardSearchResultProps(this.props)}
          />
        </SimpleTableOpProvider>
      </div>
    );
  }
}

const WrappedSimpleTableEditable: React.ComponentClass<SimpleTableEditableProps> = (() => {
  const component: React.ComponentClass<SimpleTableEditableProps> = withSimpleSpreadsheetEvaluation(
    // TODO(BNCH-5877) requestSelection is incompatible between here and withSpreadsheetEval
    // @ts-expect-error
    withSpreadsheetEvaluation<SimpleTableEditableProps & ComponentProps>(SimpleTableEditable)
  );
  component.defaultProps = SimpleTableEditable.defaultProps;
  return component;
})();

export const standaloneProps = {
  ...SimpleTableEditable.defaultProps,
  ...SimpleTableEditable.standaloneProps,
};

export default WrappedSimpleTableEditable;
